/**
 * StakeholderPicker (Sprint 4.3.8).
 *
 * Combobox surface used by the PathwayInspector when the selected node
 * is an Action with assignee semantics. The picker:
 *   - reads stakeholders from `useStakeholdersStore` (hydrating on
 *     first mount from the fixture seed),
 *   - lets the user search by name + role,
 *   - filters by Scrips role tier via a tier badge filter row,
 *   - commits the selection upward through `onSelect(id)`,
 *   - surfaces a separation-of-duty warning per Phase B
 *     `stakeholder-model.md` when the candidate role conflicts with an
 *     existing assignee on the same pathway,
 *   - exposes a "Create a new stakeholder" button when the search yields
 *     no hits, opening the NewStakeholderDialog.
 *
 * Source anchors:
 *   - Awell domain model file 03 (vault `03-knowledge-base/external-
 *     references/awell/orchestration/api-reference/overview/domain-model.md`)
 *   - Phase B `stakeholder-model.md` (Scrips role-tier + separation-of-
 *     duty)
 *
 * Brand-Patch v2: zero `\bTLP[1-4]\b` user-facing.
 *
 * HC2 token-only. HC5 zero em / en dashes. HC6 a11y: combobox uses
 * `aria-expanded`, `aria-controls`, `role="combobox"` for the input and
 * `role="listbox"` + `role="option"` for the dropdown. Keyboard
 * navigation: ArrowDown / ArrowUp move highlight; Enter commits the
 * highlighted option.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import {
  detectSeparationConflict,
  useStakeholdersStore,
} from '../../state/stakeholders.store';
import {
  SCRIPS_ROLE_TIERS,
  SCRIPS_ROLE_TIER_LABELS,
  STAKEHOLDER_ROLE_LABELS,
  type ScripsRoleTier,
  type Stakeholder,
} from '../../types/stakeholder';
import { NewStakeholderDialog } from './NewStakeholderDialog';

export interface StakeholderPickerProps {
  /**
   * Currently assigned stakeholder id (if any). Surfaced as the
   * highlighted row + the dropdown's display value.
   */
  selectedStakeholderId?: string | null;
  /**
   * Stakeholder ids already assigned elsewhere on this pathway. Used to
   * detect separation-of-duty conflicts per Phase B
   * `stakeholder-model.md`. Empty array means no other assignments.
   */
  otherAssignedStakeholderIds?: ReadonlyArray<string>;
  /** Commit a stakeholder selection upstream. */
  onSelect: (stakeholderId: string) => void;
  /** Optional: clear the assignment. */
  onClear?: () => void;
  className?: string;
}

const NO_TIER_FILTER = 'all' as const;

type TierFilter = typeof NO_TIER_FILTER | ScripsRoleTier;

export function StakeholderPicker({
  selectedStakeholderId,
  otherAssignedStakeholderIds,
  onSelect,
  onClear,
  className,
}: StakeholderPickerProps) {
  const stakeholders = useStakeholdersStore((s) => s.stakeholders);
  const hydrate = useStakeholdersStore((s) => s.hydrate);
  const titleId = useId();
  const inputId = useId();
  const listboxId = useId();
  const tierGroupId = useId();
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<TierFilter>(NO_TIER_FILTER);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return stakeholders.filter((s) => {
      if (tierFilter !== NO_TIER_FILTER && s.tier !== tierFilter) return false;
      if (q.length === 0) return true;
      return (
        s.name.toLowerCase().includes(q) ||
        s.role.toLowerCase().includes(q) ||
        STAKEHOLDER_ROLE_LABELS[s.role].toLowerCase().includes(q)
      );
    });
  }, [stakeholders, search, tierFilter]);

  const selected =
    selectedStakeholderId !== null && selectedStakeholderId !== undefined
      ? stakeholders.find((s) => s.id === selectedStakeholderId) ?? null
      : null;

  const others = (otherAssignedStakeholderIds ?? [])
    .map((id) => stakeholders.find((s) => s.id === id))
    .filter((s): s is Stakeholder => Boolean(s));

  const conflict = selected
    ? detectSeparationConflict(selected, others)
    : null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlightIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filtered[highlightIndex];
      if (target) {
        onSelect(target.id);
        setOpen(false);
        setSearch('');
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div
      data-testid="stakeholder-picker"
      className={[
        'flex flex-col gap-1 rounded-signal-md border border-surface-border bg-surface-base p-gutter-sm',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span
        id={titleId}
        className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
      >
        Assigned to
      </span>
      {selected ? (
        <div
          data-testid="stakeholder-picker-selected"
          className="flex items-center justify-between gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-muted px-gutter-sm py-1"
        >
          <div className="flex flex-col">
            <span className="text-signal-sm font-semibold text-text-primary">
              {selected.name}
            </span>
            <span className="text-signal-xs text-text-tertiary">
              {STAKEHOLDER_ROLE_LABELS[selected.role]}
              {' . '}
              {SCRIPS_ROLE_TIER_LABELS[selected.tier]}
            </span>
          </div>
          {onClear ? (
            <button
              type="button"
              data-testid="stakeholder-picker-clear"
              onClick={onClear}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-0.5 text-signal-xs font-semibold text-text-secondary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            >
              Clear
            </button>
          ) : null}
        </div>
      ) : null}

      {conflict ? (
        <p
          role="alert"
          data-testid="stakeholder-picker-separation-warning"
          className="rounded-signal-md border border-warning-soft-bg bg-warning-soft-bg px-gutter-sm py-1 text-signal-xs text-warning-soft-fg"
        >
          Stakeholder {conflict.candidate.name} (
          {STAKEHOLDER_ROLE_LABELS[conflict.candidate.role]}) is incompatible
          with already-assigned {conflict.incumbent.name} (
          {STAKEHOLDER_ROLE_LABELS[conflict.incumbent.role]}) on this
          enrollment per Scrips role-tier policy. Proceed anyway?
        </p>
      ) : null}

      <div
        role="group"
        aria-labelledby={tierGroupId}
        className="flex flex-wrap items-center gap-1"
      >
        <span
          id={tierGroupId}
          className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Tier
        </span>
        <button
          type="button"
          data-testid="stakeholder-picker-tier-all"
          onClick={() => setTierFilter(NO_TIER_FILTER)}
          aria-pressed={tierFilter === NO_TIER_FILTER}
          className={[
            'rounded-signal-pill px-gutter-sm py-0.5 text-signal-xs font-semibold uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-signal-primary-500',
            tierFilter === NO_TIER_FILTER
              ? 'bg-signal-primary-500 text-text-inverse'
              : 'bg-surface-muted text-text-secondary hover:bg-surface-base',
          ].join(' ')}
        >
          All
        </button>
        {SCRIPS_ROLE_TIERS.map((tier) => (
          <button
            key={tier}
            type="button"
            data-testid={`stakeholder-picker-tier-${tier}`}
            onClick={() => setTierFilter(tier)}
            aria-pressed={tierFilter === tier}
            className={[
              'rounded-signal-pill px-gutter-sm py-0.5 text-signal-xs font-semibold uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-signal-primary-500',
              tierFilter === tier
                ? 'bg-signal-primary-500 text-text-inverse'
                : 'bg-surface-muted text-text-secondary hover:bg-surface-base',
            ].join(' ')}
          >
            {SCRIPS_ROLE_TIER_LABELS[tier]}
          </button>
        ))}
      </div>

      <div className="relative">
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-labelledby={titleId}
          autoComplete="off"
          data-testid="stakeholder-picker-input"
          placeholder="Search stakeholders by name or role"
          value={search}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
            setHighlightIndex(0);
          }}
          onKeyDown={handleKeyDown}
          className="w-full rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        />
        {open ? (
          <ul
            id={listboxId}
            role="listbox"
            data-testid="stakeholder-picker-listbox"
            className="absolute left-0 right-0 z-10 mt-1 flex max-h-60 flex-col gap-0.5 overflow-y-auto rounded-signal-md border border-surface-border bg-surface-base p-1 shadow-signal-elevated"
          >
            {filtered.length === 0 ? (
              <li
                data-testid="stakeholder-picker-empty"
                className="flex flex-col gap-1 rounded-signal-md bg-surface-muted px-gutter-sm py-1 text-signal-xs text-text-tertiary"
              >
                <span>No stakeholders match the search.</span>
                <button
                  type="button"
                  data-testid="stakeholder-picker-create-new"
                  onClick={() => {
                    setOpen(false);
                    setDialogOpen(true);
                  }}
                  className="self-start rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-0.5 text-signal-xs font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
                >
                  Create a new stakeholder
                </button>
              </li>
            ) : (
              filtered.map((s, i) => {
                const isHighlighted = i === highlightIndex;
                const isSelected = selected?.id === s.id;
                return (
                  <li
                    key={s.id}
                    role="option"
                    aria-selected={isSelected}
                    data-testid={`stakeholder-picker-option-${s.id}`}
                    data-highlighted={isHighlighted}
                    onMouseEnter={() => setHighlightIndex(i)}
                    onClick={() => {
                      onSelect(s.id);
                      setOpen(false);
                      setSearch('');
                    }}
                    className={[
                      'flex cursor-pointer flex-col gap-0.5 rounded-signal-md px-gutter-sm py-1 text-signal-sm',
                      isHighlighted
                        ? 'bg-surface-muted'
                        : 'bg-surface-base',
                      isSelected
                        ? 'border border-signal-primary-500'
                        : 'border border-transparent',
                    ].join(' ')}
                  >
                    <span className="font-semibold text-text-primary">
                      {s.name}
                    </span>
                    <span className="text-signal-xs text-text-tertiary">
                      {STAKEHOLDER_ROLE_LABELS[s.role]}
                      {' . '}
                      {SCRIPS_ROLE_TIER_LABELS[s.tier]}
                    </span>
                  </li>
                );
              })
            )}
            <li className="border-t border-surface-border pt-1">
              <button
                type="button"
                data-testid="stakeholder-picker-open-dialog"
                onClick={() => {
                  setOpen(false);
                  setDialogOpen(true);
                }}
                className="w-full rounded-signal-md bg-surface-base px-gutter-sm py-1 text-left text-signal-xs font-semibold text-signal-primary-500 hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
              >
                + Create a new stakeholder
              </button>
            </li>
          </ul>
        ) : null}
      </div>

      <NewStakeholderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={(stakeholder) => {
          setDialogOpen(false);
          onSelect(stakeholder.id);
        }}
      />
    </div>
  );
}
